import { useTranslation } from 'react-i18next';
import type { MenuItem } from '@/api/types';
import { useLayout } from '@/theme/useLayout';
import { AppLogo } from './AppLogo';
import { NavMenu } from './NavMenu';

/**
 * The navigation sidebar (`_Sidebar.cshtml`).
 *
 * `#js-primary-nav`, `.primary-nav` and `.custom-scroll` are all selected on by
 * the stylesheet, so they stay. The `js-nav-built` class the plugin used to add
 * once it had rewritten the markup is gone: nothing styles it, and there is no
 * "before JS ran" state to guard against now.
 *
 * The backdrop below the aside closes the mobile off-canvas menu, which is the
 * only thing its `data-action="toggle-swap"` did.
 */
export function Sidebar({ items }: { items: MenuItem[] }) {
  const { t } = useTranslation();
  // Whether the off-canvas menu is open is not read here: `app-mobile-menu-open`
  // on the html element is what slides the aside in, and LayoutProvider owns it.
  const { setMobileMenuOpen } = useLayout();

  return (
    <>
      <aside className="app-sidebar d-flex flex-column">
        <AppLogo />

        {/* No filter box: the menu is a handful of entries, so a search over it
            earned nothing. Going with it: the "no menu items found / press Esc to
            reset" panel, which only ever appeared when a filter hid everything. */}
        <nav id="js-primary-nav" className="primary-nav flex-grow-1 custom-scroll">
          <NavMenu items={items} />
        </nav>

        <div className="nav-footer">
          <svg className="sa-icon sa-thin">
            <use href="/myui/icons/sprite.svg#wifi" />
          </svg>
        </div>
      </aside>

      {/* `data-toggleclass` is a styling hook as well as the old runtime hook:
          `[data-toggleclass="app-mobile-menu-open"].backdrop` is what reveals
          this backdrop, and the drawers' `.open + .backdrop:not([data-toggleclass])`
          rule depends on only this one carrying it. */}
      <button
        type="button"
        className="backdrop"
        data-toggleclass="app-mobile-menu-open"
        onClick={() => setMobileMenuOpen(false)}
        aria-label={t('CloseMenu', 'Close menu')}
      />
    </>
  );
}
